/**
 * The realm, as a graph of provinces.
 *
 * This is the strategic board: every holding, the roads and fords and stairs
 * between them, and what each one is worth to whoever is holding it. The
 * campaign layer reads this table once at the start of a campaign and then owns
 * a *mutable copy* of it, because who holds what is state, not content.
 *
 * The table itself is no longer written out by hand. It is **generated from a
 * fixed seed** in `realm.ts`: a hand-authored heartland of twenty-four holdings
 * on the eastern seaboard — Ashfall, Deepgate, Stonepale and the rest, unchanged
 * in name, terrain, yields and graph — with a frontier grown around it out to a
 * hundred and twenty-five provinces in all. Generation is deterministic and the
 * seed is a constant, so the realm is the same realm in every build and on every
 * machine. If you want to change the size or the shape of the board, `realm.ts`
 * is where the dials are; this file is the reader.
 *
 * `x` and `y` are layout coordinates for the world-map screen and have no
 * simulation meaning; the graph is the `links`, not the positions. They run to
 * roughly 500 by 390 — see `WORLD` in `realm.ts` — which is a bigger sheet of
 * paper than the old 0..100, drawn at the same *scale*. That is deliberate: the
 * provinces are about the size they always were and there are simply more of
 * them, which is what let every tuned constant in `cartography.ts` survive.
 */

import { LinkKind, REALM } from './realm.ts';

export {
  LinkKind,
  LINK_NAMES,
  LINK_MARCH,
  PROVINCE_TERRAIN,
  WORLD,
  RASTER,
  SEA_CELLS,
  SEA_LANES,
  REGIONS,
  regionOf,
  regionDef,
  regionById,
  nationsInRegion,
  regionsOfCharacter,
  // ── Who lives where ─────────────────────────────────────────────────────
  // The join between the map and `races.ts`. A region exposes a `Homeland`, a
  // crown exposes a `raceId` and a `leaderId`, and these four read it back:
  // which regions suit a people, which peoples a region suits, which crowns a
  // people holds, and where a people ended up. See the note over
  // `regionsOfHomeland` in `realm.ts`.
  regionsOfHomeland,
  racesOfRegion,
  nationsOfRace,
  seatOfRace,
} from './realm.ts';
export type {
  LinkKindId,
  ProvinceTerrain,
  TerrainProfile,
  ProvinceDef,
  RealmRaster,
  SeaCellDef,
  SeaLaneDef,
  RegionDef,
  RegionCharacter,
} from './realm.ts';

import type { LinkKindId, ProvinceDef } from './realm.ts';

export const PROVINCES: ProvinceDef[] = REALM.provinces;

/**
 * Links that are something other than open country. Everything not listed here
 * is `Wild`. Order within a pair does not matter.
 */
export const SPECIAL_LINKS: Array<{ a: string; b: string; kind: LinkKindId }> = REALM.specialLinks;

const BY_ID = new Map<string, number>();
PROVINCES.forEach((def, i) => BY_ID.set(def.id, i));

/**
 * Link kinds, by pair, resolved once.
 *
 * `linkKindFor` used to walk a list of sixteen. The generated realm declares a
 * few hundred, and the campaign builder asks about every edge on the board at
 * start-up, so the walk was about to become the most expensive thing in
 * `createCampaign` for no reason at all.
 */
const LINK_KIND = new Map<string, LinkKindId>();
for (const s of SPECIAL_LINKS) {
  LINK_KIND.set(`${s.a}|${s.b}`, s.kind);
  LINK_KIND.set(`${s.b}|${s.a}`, s.kind);
}

export function provinceIndex(id: string): number {
  const i = BY_ID.get(id);
  return i === undefined ? -1 : i;
}

export function provinceDef(i: number): ProvinceDef {
  return PROVINCES[i] ?? PROVINCES[0];
}

/** Look up the kind of a declared link. Unlisted pairs are open country. */
export function linkKindFor(a: string, b: string): LinkKindId {
  return LINK_KIND.get(`${a}|${b}`) ?? LinkKind.Wild;
}

export const PROVINCE_COUNT = PROVINCES.length;

/** How many of the provinces are the hand-authored heartland, and they come first. */
export const HEARTLAND_COUNT = REALM.heartlandCount;

/**
 * Does this holding stand on navigable water?
 *
 * The gate on the whole sea layer, and a property of the *realm* rather than of
 * the drawing — `cartography.isCoastal` reads this rather than the other way
 * round. Only a coastal province may raise a port, only a coastal province may
 * be landed on from the sea, and only coastal provinces carry sea lanes.
 */
export function isCoastalProvince(i: number): boolean {
  return PROVINCES[i]?.coastal ?? false;
}

/** No road reaches this holding. There are two, and they are in the Sundered Sea. */
export function isIslandProvince(i: number): boolean {
  return PROVINCES[i]?.island ?? false;
}
